import React, { useMemo } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { AppTimeField } from '../../components/AppTimeField';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAppColors } from '../../context/PreferencesContext';
import type { TimeLogEmployeeChoice } from '../../hooks/useTimeLogEmployee';
import { formatDurationCompact } from '../../utils/projectTaskForm';
import { timeLogDurationMinutes } from '../../utils/taskTimeLog';

type Props = {
  startTime: string;
  endTime: string;
  onChangeStart: (value: string) => void;
  onChangeEnd: (value: string) => void;
  error?: string;
  required?: boolean;
  /** Shows the employee chips for HR Admins / Team Leads. */
  employee?: TimeLogEmployeeChoice;
};

/**
 * From Time / To Time pickers with the duration worked out from the range.
 * The duration is display-only; the API calculates the stored minutes.
 */
export function TimeEntryFields({
  startTime,
  endTime,
  onChangeStart,
  onChangeEnd,
  error,
  required,
  employee,
}: Props) {
  const palette = useAppColors();
  const themed = useMemo(
    () => ({
      meta: { ...typography.caption, color: palette.textSecondary },
      duration: { ...typography.body, color: palette.textPrimary, fontWeight: '700' as const },
      error: { ...typography.caption, color: palette.error },
    }),
    [palette],
  );
  const minutes = timeLogDurationMinutes(startTime, endTime);
  const suffix = required ? ' *' : '';

  return (
    <View style={styles.container}>
      {employee?.canLogForOthers ? (
        <View style={styles.employeeField}>
          <Text style={themed.meta}>Employee</Text>
          {employee.options.length ? (
            <View style={styles.employeeRow}>
              {employee.options.map(option => {
                const selected = option.employeeId === employee.selectedEmployeeId;
                return (
                  <Pressable
                    key={option.employeeId}
                    accessibilityRole="button"
                    accessibilityLabel={`Log time for ${option.employeeName}`}
                    accessibilityState={{ selected }}
                    onPress={() => employee.select(option.employeeId)}
                    style={[
                      styles.employeeChip,
                      {
                        borderColor: selected ? palette.primary : palette.border,
                        backgroundColor: selected ? palette.primary : palette.surface,
                      },
                    ]}
                  >
                    <Text
                      style={[
                        themed.meta,
                        { color: selected ? palette.onPrimary : palette.textPrimary },
                      ]}
                    >
                      {option.employeeId === employee.selfEmployeeId
                        ? `${option.employeeName} (you)`
                        : option.employeeName}
                    </Text>
                  </Pressable>
                );
              })}
            </View>
          ) : (
            <Text style={themed.error}>{employee.missingMessage}</Text>
          )}
        </View>
      ) : null}
      <View style={styles.timeRow}>
        <View style={styles.timeField}>
          <AppTimeField
            label={`From Time${suffix}`}
            value={startTime}
            onChange={onChangeStart}
          />
        </View>
        <View style={styles.timeField}>
          <AppTimeField
            label={`To Time${suffix}`}
            value={endTime}
            onChange={onChangeEnd}
          />
        </View>
      </View>
      <View style={styles.durationRow}>
        <Text style={themed.meta}>Time spent</Text>
        <Text accessibilityLabel="Calculated time spent" style={themed.duration}>
          {minutes != null ? formatDurationCompact(minutes) : '—'}
        </Text>
      </View>
      {error ? (
        <Text accessibilityRole="alert" style={themed.error}>
          {error}
        </Text>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  container: { gap: spacing.sm },
  employeeField: { gap: spacing.xs },
  employeeRow: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.xs },
  employeeChip: {
    borderWidth: 1,
    borderRadius: 16,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.xs,
  },
  // Side by side when there is room; stacked on narrow phones.
  timeRow: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm },
  timeField: { flexGrow: 1, flexBasis: 140, minWidth: 140 },
  durationRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
});
